// Ô "Khu vực": bấm vào chọn từng bước — Tỉnh → Quận → Phường (địa chỉ cũ) hoặc Tỉnh → Phường
// (địa chỉ mới). Danh mục từ @shoptrack/common/admin-areas. Điện thoại: mở toàn màn hình.

import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, RotateCw } from 'lucide-react'
import { removeVietnameseAccents } from '@shoptrack/common/geo'
import type { AddressMode } from '@shoptrack/common/admin-areas'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { SearchInput } from '@/components/ui/search-input'
import type { AdminAreas } from '@/hooks/useAdminAreas'

export interface PickedArea {
  city: string
  district: string | null
  ward: string
}

type Step = 'city' | 'district' | 'ward'
const STEP_LABEL: Record<Step, string> = { city: 'Tỉnh / TP', district: 'Quận / huyện', ward: 'Phường / xã' }

/** Viết tắt tiền tố như nhãn vận đơn — ô hẹp vẫn đọc được. */
const SHORT_PREFIX: Array<[RegExp, string]> = [
  [/^Thành phố\s+/i, 'TP. '],
  [/^Thị trấn\s+/i, 'TT. '],
  [/^Thị xã\s+/i, 'TX. '],
  [/^Quận\s+/i, 'Q. '],
  [/^Huyện\s+/i, 'H. '],
  [/^Phường\s+/i, 'P. '],
  [/^Xã\s+/i, 'X. '],
  [/^Tỉnh\s+/i, ''],
]

/** "Thành phố Thủ Đức" → "TP. Thủ Đức". Chỉ để hiển thị. */
export function shortAreaName(name: string): string {
  for (const [re, short] of SHORT_PREFIX) if (re.test(name)) return name.replace(re, short)
  return name
}

/** "P. Bến Nghé, Q. 1, Hồ Chí Minh" — chỉ để hiển thị. */
export function areaLabel(a: { city?: string | null; district?: string | null; ward?: string | null } | null): string {
  if (!a) return ''
  return [a.ward, a.district, a.city]
    .filter((s): s is string => Boolean(s?.trim()))
    .map(shortAreaName)
    .join(', ')
}

export function AreaPicker({
  areas,
  mode,
  label,
  onPick,
  disabled,
  invalid,
  id,
  loadFailed,
}: {
  /** undefined = danh mục đang tải (hoặc tải lỗi — xem loadFailed) */
  areas: AdminAreas | undefined
  mode: AddressMode
  /** Chữ hiện trên ô (nơi gọi tính theo chế độ); rỗng = chưa chọn */
  label: string
  onPick: (picked: PickedArea) => void
  disabled?: boolean
  invalid?: boolean
  id?: string
  /** Tải danh mục lỗi (vd. bản web cũ sau khi deploy) — import() lỗi không tự thử lại được, phải tải lại trang */
  loadFailed?: boolean
}) {
  const steps: Step[] = mode === 'old' ? ['city', 'district', 'ward'] : ['city', 'ward']
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [city, setCity] = useState('')
  const [district, setDistrict] = useState('')
  const [query, setQuery] = useState('')
  const current = steps[Math.min(step, steps.length - 1)]!

  const options = useMemo(() => {
    if (!areas || !open) return []
    if (current === 'city') return areas.listProvinces(mode)
    if (current === 'district') return areas.listOldDistricts(city)
    return mode === 'new' ? areas.listNewWards(city) : areas.listOldWards(city, district)
  }, [areas, open, current, mode, city, district])

  const q = removeVietnameseAccents(query)
  const shown = q ? options.filter((o) => removeVietnameseAccents(o).includes(q)) : options

  function start() {
    setStep(0)
    setCity('')
    setDistrict('')
    setQuery('')
    setOpen(true)
  }

  function choose(name: string) {
    setQuery('')
    if (current === 'city') {
      setCity(name)
      setDistrict('')
      setStep(1)
    } else if (current === 'district') {
      setDistrict(name)
      setStep(2)
    } else {
      onPick({ city, district: mode === 'old' ? district : null, ward: name })
      setOpen(false)
    }
  }

  const placeholder = mode === 'old' ? 'Chọn Tỉnh → Quận → Phường' : 'Chọn Tỉnh → Phường / xã'

  if (loadFailed && !areas) {
    return (
      <button
        type="button"
        id={id}
        onClick={() => window.location.reload()}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-danger bg-paper px-3.5 text-left text-base text-danger sm:text-sm desktop:h-9"
      >
        <span className="truncate">Không tải được danh mục khu vực</span>
        <RotateCw className="size-4 shrink-0" />
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        id={id}
        disabled={disabled || !areas}
        onClick={start}
        aria-invalid={invalid || undefined}
        aria-haspopup="dialog"
        className={cn(
          'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-rule bg-paper px-3.5 text-left text-base sm:text-sm desktop:h-9',
          'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20',
          'disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger',
          !label && 'text-muted',
        )}
      >
        <span className="truncate">{label || placeholder}</span>
        {areas ? (
          <ChevronDown className="size-4 shrink-0 text-muted" />
        ) : (
          <Loader2 className="size-4 shrink-0 animate-spin text-accent" aria-label="Đang tải danh mục" />
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col gap-3 sm:max-w-md max-sm:inset-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-t-0">
          <DialogTitle className="pr-10 text-base">
            {mode === 'old' ? 'Khu vực — địa chỉ cũ' : 'Khu vực — địa chỉ mới'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Chọn lần lượt {steps.map((s) => STEP_LABEL[s]).join(', ')}
          </DialogDescription>

          <nav className="flex flex-wrap items-center gap-1 text-xs" aria-label="Các bước">
            {steps.map((s, i) => (
              <span key={s} className="inline-flex items-center gap-1">
                {i > 0 && <ChevronRight className="size-3 text-muted" />}
                <button
                  type="button"
                  disabled={i > step}
                  onClick={() => {
                    setStep(i)
                    setQuery('')
                  }}
                  className={cn(
                    'min-h-9 rounded px-1.5',
                    i === step ? 'bg-accent/10 font-medium text-accent' : 'text-inkSoft enabled:hover:text-ink disabled:opacity-50',
                  )}
                >
                  {i < step ? shortAreaName(i === 0 ? city : district) : STEP_LABEL[s]}
                </button>
              </span>
            ))}
          </nav>

          <SearchInput
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && shown[0]) {
                e.preventDefault()
                choose(shown[0])
              }
            }}
            placeholder={`Tìm ${STEP_LABEL[current].toLowerCase()}…`}
          />

          <ul className="-mx-1 min-h-0 flex-1 overflow-y-auto sm:max-h-[50dvh]" aria-label={STEP_LABEL[current]}>
            {shown.map((name) => (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => choose(name)}
                  className="flex min-h-11 w-full items-center rounded-md px-2.5 text-left text-sm hover:bg-paperSoft desktop:min-h-9"
                >
                  {name}
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2.5 py-6 text-center text-sm text-muted">Không tìm thấy</li>}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  )
}
